import { AppstoreAddOutlined } from '@ant-design/icons';
import React, { useMemo } from 'react';
import { useStore } from 'zustand';

import type {
  Experience,
  ExperienceConfiguration,
  PackExperiencesResult,
  SubHeaderRow,
} from '../../../ExtranetPackages.types';
import { PackType, packagesStore } from '../../../PackageStore';
import { CustomHeader } from '../../GenericTable/CustomHeader';
import { GenericTable } from '../../GenericTable/GenericTable';
import {
  BadgeCell,
  ImageTextCell,
  PriceCell,
  TagCell,
  TextCell,
} from '../../GenericTable/TableCellRenderers';
import { useTableColumns } from '../../GenericTable/useTableColumns';

import styles from './ExperiencesTable.module.scss';
import type { PricingTotals } from './PricingTotals.types';
import { PricingTotalsRow } from './PricingTotalsRow';

export type ExperienceRow = Experience | SubHeaderRow;

const isSubHeaderRow = (record: ExperienceRow): record is SubHeaderRow =>
  'isSubHeader' in record && record.isSubHeader === true;

type ExperiencesTableProps = {
  dataSource?: PackExperiencesResult;
  pricingTotals?: PricingTotals;
  loading?: boolean;
  hideChildPriceColumn?: boolean;
  minChargedPax?: number;
  maxAllowedPax?: number;
  subHeader?: string;
  subHeaderOvernight?: string;
};

export const ExperiencesTable: React.FC<ExperiencesTableProps> = ({
  dataSource = { alwaysIncludedXps: [], includedExperience: [] },
  pricingTotals,
  loading,
  hideChildPriceColumn = false,
  minChargedPax,
  maxAllowedPax,
  subHeader,
  subHeaderOvernight,
}) => {
  const totalCols = hideChildPriceColumn ? 4 : 5;

  const packType = useStore(packagesStore, (state) => state.packType);

  const isDaytime = packType === PackType.DAYTIME;

  const enrichedDataSource = useMemo(() => {
    const regular = dataSource?.includedExperience || [];
    const overnight = dataSource?.alwaysIncludedXps || [];

    const result: ExperienceRow[] = [];

    if (subHeader && regular.length > 0) {
      result.push({
        id: '__subheader__',
        name: subHeader,
        isSubHeader: true,
      });
    }

    result.push(...regular);

    if (subHeaderOvernight && overnight.length > 0) {
      result.push({
        id: '__subheader_overnight__',
        name: subHeaderOvernight,
        isSubHeader: true,
      });
    }

    result.push(...overnight);

    return result;
  }, [dataSource, subHeader, subHeaderOvernight]);

  const columnsConfig = useMemo(
    () => [
      {
        key: 'name',
        title: 'Activity name',
        onCell: (record: ExperienceRow) =>
          isSubHeaderRow(record) ? { colSpan: totalCols } : {},
        render: (_: unknown, record: ExperienceRow) => {
          if (isSubHeaderRow(record)) {
            return <span className={styles.subHeaderLabel}>{record.name}</span>;
          }

          return (
            <ImageTextCell
              images={record.images}
              title={record.name}
              tooltip={
                record.isDailyPrice
                  ? 'Prices for this activity depend on the day of the week. The price shown is the lowest available.'
                  : undefined
              }
            />
          );
        },
      },
      {
        key: 'adultPrice',
        title: 'Adult',
        width: '140px',
        onCell: (record: ExperienceRow): object =>
          isSubHeaderRow(record) ? { colSpan: 0 } : {},
        render: (value: number, record: ExperienceRow) => {
          if (isSubHeaderRow(record)) {
            return null;
          }

          if (record.areAdultsAllowed === false) {
            return <TextCell value="Not allowed" variant="grey" />;
          }

          return <PriceCell value={value} />;
        },
      },
      ...(hideChildPriceColumn
        ? []
        : [
            {
              key: 'childPrice',
              title: (
                <CustomHeader text="Child" tooltip="Child price (ages 2–12)" />
              ),
              width: '140px',
              onCell: (record: ExperienceRow) =>
                isSubHeaderRow(record) ? { colSpan: 0 } : {},
              render: (value: number, record: ExperienceRow) => {
                if (isSubHeaderRow(record)) {
                  return null;
                }

                if (record.areChildrenAllowed === false) {
                  return <TextCell value="Not allowed" variant="grey" />;
                }

                if (value !== undefined) {
                  return <PriceCell value={value} />;
                }

                if (record.configuration?.isPerPerson) {
                  return <PriceCell value={record.adultPrice} />;
                }

                return <TextCell value="-" variant="grey" />;
              },
            },
          ]),
      {
        key: 'configuration',
        title: 'Configuration',
        width: '200px',
        onCell: (record: ExperienceRow): object =>
          isSubHeaderRow(record) ? { colSpan: 0 } : {},
        render: (value: ExperienceConfiguration, record: ExperienceRow) => {
          if (isSubHeaderRow(record) || record.isAlwaysIncludedOvernight) {
            return null;
          }

          const { quantity, isPerPerson, isPerNight } = value;
          const unit = isPerPerson ? 'per pers.' : 'unit';
          const period = !isDaytime
            ? isPerNight
              ? 'per night'
              : 'per stay'
            : undefined;

          return <TagCell values={[quantity, unit, period]} />;
        },
      },
      {
        key: 'mustHave',
        title: (
          <CustomHeader
            text="Must-have"
            tooltip="When a must-have activity is unavailable, the package may no longer be available for sale."
          />
        ),
        width: '125px',
        align: 'center' as const,
        onCell: (record: ExperienceRow): object =>
          isSubHeaderRow(record) ? { colSpan: 0 } : {},
        render: (_: unknown, record: ExperienceRow) => {
          if (isSubHeaderRow(record)) {
            return null;
          }

          return record.configuration?.isOptional === false &&
            !record.isAlwaysIncludedOvernight ? (
            <BadgeCell />
          ) : (
            <span>-</span>
          );
        },
      },
    ],
    [hideChildPriceColumn, isDaytime, totalCols]
  );

  const columns = useTableColumns(columnsConfig);

  return (
    <GenericTable
      title="Activities & amenities"
      columns={columns}
      dataSource={enrichedDataSource}
      loading={loading}
      emptyState={{
        title: 'No activities',
        description:
          'This package has no included activities. Contact your Account Manager for more information.',
        icon: <AppstoreAddOutlined />,
      }}
      summary={
        pricingTotals
          ? () => (
              <PricingTotalsRow
                data={pricingTotals}
                colSpan={columnsConfig.length}
                minChargedPax={minChargedPax}
                maxAllowedPax={maxAllowedPax}
              />
            )
          : undefined
      }
    />
  );
};
